import {
  chain as _chain,
  some as _some,
  map as _map,
  isEmpty as _isEmpty,
  every as _every,
  isArray as _isArray,
} from 'lodash'

import normalizeUrl from 'normalize-url'
import { List } from 'immutable'
import qs from 'qs'
import { captureException } from 'utils/SentryHelpers'

const isAnyOf = (values, state) => _some(values, (val) => state === val)

const notIsAnyOf = (values, state) => !isAnyOf(values, state)

const is = (value, state) => value === state

const notIs = (value, state) => value !== state

const isMatch = (values, state = '') => {
  if (!_isArray(values)) {
    values = [values]
  }

  return _some(values.map(value => new RegExp(value)), (val) => val.test(state))
}

const notIsMatch = (values, state = '') => !isMatch(values, state)

const includesAnyOf = (values, state = '') => {
  return _some(values, (val) => state.indexOf(val) !== -1)
}

const notIncludesAnyOf = (values, state = '') =>
  _every(values, (val) => state.indexOf(val) === -1)


const url = (operand, value, testUrl) => {
  return operand(value, testUrl)
}

const urlPath = (operand, value, testPathname) => {
  return operand(value, testPathname)
}

const urlHost = (operand, value, testHostname) => {
  return operand(value, testHostname)
}

const urlQuery = (operand, value, testSearchParam) => {
  // coerce to array
  if (!_isArray(value)) {
    value = [value]
  }

  // group by key so we can check one param at a time with the specified operator
  const valuesByKey = _chain(value)
    .filter((keyAndValue) => keyAndValue.indexOf('=') > 0)
    .map((keyAndValue) => keyAndValue.split('=', 2))
    .map((keyAndValue) => {
      return {
        key: keyAndValue[0].toLowerCase(),
        val: keyAndValue[1],
      }
    })
    .groupBy('key')
    .mapValues((values) => _map(values, 'val'))
    .value()

  // only happens if there was an invalid key
  if (_isEmpty(valuesByKey)) {
    return true
  }

  const query = qs.parse(testSearchParam)

  // values within a condition are OR'd
  // FIXME technically not true for `notIsAnyOf` and `notIncludesAnyOf`
  return _some(valuesByKey, (values, key) => {
    return operand(values, query[key])
  })
}


export const driftNormalizeUrl = (url) => {
  try {
    url = normalizeUrl(url, {
      normalizeProtocol: true, // convert '//' -> 'http://'
      stripFragment: false,
      stripWWW: true,
      removeQueryParameters: [], // default is to remove 'utm_' params
      removeTrailingSlash: true,
    })
  } catch(e) {
    console.warn(`Could not parse URL '${url}'`)
  }

  // strip protocol entirely - `normalizeProtocol` only converts to http://
  return url.replace(/^https?:\/\//, '')
}

const operands = {
  is,
  notIs,
  isMatch,
  notIsMatch,
  isAnyOf,
  notIsAnyOf,
  includesAnyOf,
  notIncludesAnyOf,
}

const validationDefinitions = {
  url: {
    fn: url,
    typeFn: driftNormalizeUrl,
    selector: anchor => anchor.href,
    operands: {
      is,
      notIs,
      isMatch,
      notIsMatch,
      isAnyOf,
      notIsAnyOf,
      includesAnyOf,
      notIncludesAnyOf,
    },
  },
  urlPath: {
    fn: urlPath,
    typeFn: driftNormalizeUrl,
    selector: anchor => anchor.pathname,
    operands: {
      is,
      notIs,
      isAnyOf,
      notIsAnyOf,
      includesAnyOf,
      notIncludesAnyOf,
    },
  },
  urlHost: {
    fn: urlHost,
    selector: anchor => anchor.hostname,
    operands: {
      is,
      notIs,
      isAnyOf,
      notIsAnyOf,
      includesAnyOf,
      notIncludesAnyOf,
    },
  },
  urlQuery: {
    fn: urlQuery,
    selector: anchor => anchor.search.slice(1), // get rid of "?"
    operands: {
      is,
      notIs,
      isAnyOf,
      notIsAnyOf,
      includesAnyOf,
      notIncludesAnyOf,
    },
  }
}

export const checkUrlValidity = (value, operator, field, testAnchor) => {
  try {
    const operand = operands[operator]
    const {
      fn,
      typeFn = (url) => url,
      selector,
    } = validationDefinitions[field]
    return fn(operand, List.isList(value) ? value.map(typeFn).toJS() : typeFn(value), typeFn(selector(testAnchor)))
  } catch(err) {
    captureException(err, {
      extra: {
        value,
        operator,
        field,
        testAnchor,
      }
    })
    return false
  }
}



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/ConditionModal/helpers.js